import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { BrandLogo } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { copyWithToast } from '@/lib/shareLaunch';
import { Share2, Download, Sparkles, Heart, ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface AffirmationData {
  id: string;
  image_url: string;
  affirmation_text: string;
  created_at: string;
  user_id: string;
}

export default function AffirmationPublicView() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<AffirmationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [creatorRefCode, setCreatorRefCode] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data: row, error } = await (supabase as any).rpc('get_public_affirmation_image', { p_id: id });
      const record = Array.isArray(row) ? row[0] : row;
      if (error || !record) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setData(record as AffirmationData);


      // Fetch creator's referral code for attribution
      const { data: profile } = await supabase
        .from('profiles')
        .select('referral_code')
        .eq('user_id', row.user_id)
        .maybeSingle();
      if (profile?.referral_code) {
        setCreatorRefCode(profile.referral_code);
        // Store in localStorage so useReferralCapture picks it up on signup,
        // but only if visitor doesn't already have a referral stored
        const REFERRAL_KEY = 'sayitnow_ref';
        if (!localStorage.getItem(REFERRAL_KEY)) {
          localStorage.setItem(REFERRAL_KEY, profile.referral_code);
        }
      }
      setLoading(false);
    })();
  }, [id]);

  /** Append ref param to a path if creator referral code is available */
  const withRef = (path: string): string => {
    if (!creatorRefCode) return path;
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}ref=${encodeURIComponent(creatorRefCode)}`;
  };

  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/affirmation/${id}`
    : '';

  useDocumentHead({
    title: 'Personalized Affirmation | Say It Now',
    description: 'Turn your photo into a powerful affirmation character and share it.',
    meta: [
      { property: 'og:image', content: data?.image_url ?? '' },
      { property: 'og:url', content: shareUrl },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: data?.image_url ?? '' },
    ],
  });

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Personalized Affirmation | Say It Now',
          text: 'Check out my personalized affirmation!',
          url: shareUrl,
        });
      } catch {
        // user cancelled
      }
    } else {
      await copyWithToast(shareUrl, 'Link copied!');
    }
  };

  const handleDownload = async () => {
    if (!data?.image_url) return;
    try {
      const res = await fetch(data.image_url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `affirmation-${id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Image downloaded!');
    } catch {
      toast.error('Download failed. Try saving the image directly.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-6 text-center">
        <Heart className="h-12 w-12 text-muted-foreground/40" />
        <h1 className="text-xl font-bold text-foreground">Affirmation not found</h1>
        <p className="text-sm text-muted-foreground">This affirmation may have been removed or the link is invalid.</p>
        <Button asChild>
          <Link to="/">Go Home</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Ambient background glow */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-primary/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-0 w-[300px] h-[300px] bg-primary/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10">
        {/* Brand Bar */}
        <header className="flex items-center justify-between px-5 py-4 border-b border-border/20">
          <BrandLogo size="xs" />
        </header>

        <main className="max-w-lg mx-auto px-5 pb-16">
          {/* Hero */}
          <section className="text-center pt-8 pb-6">
            <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight mb-2">
              Your affirmation. <span className="text-primary">Your power.</span>
            </h1>
            <p className="text-sm text-muted-foreground">Created on Say It Now</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Turn your photo into a self-affirmation character
            </p>
          </section>

          {/* Main Image */}
          <section className="mb-6">
            <div className="relative rounded-2xl overflow-hidden shadow-elevation-3 border border-border/20">
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-primary/10 pointer-events-none z-10" />
              <AspectRatio ratio={4 / 5}>
                <img
                  src={data.image_url}
                  alt={data.affirmation_text || 'Personalized affirmation image'}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </AspectRatio>
            </div>
          </section>

          {/* Emotional Message */}
          <section className="text-center mb-8 space-y-2">
            <p className="text-sm sm:text-base text-foreground font-medium leading-relaxed px-2">
              This is how it starts — with you.
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground italic leading-relaxed px-2">
              Now imagine what your words could mean to someone else.
            </p>
          </section>

          {/* Primary CTA */}
          <section className="mb-4">
            <Button asChild size="lg" className="w-full text-base gap-2">
              <Link to={withRef('/signup')}>
                <Sparkles className="h-5 w-5" />
                Create Your Own
              </Link>
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Turn your photo into a shareable affirmation character
            </p>
          </section>

          {/* Remix CTA */}
          <section className="mb-6 text-center">
            <p className="text-xs text-muted-foreground mb-2">Love this style?</p>
            <Button asChild variant="outline" className="w-full gap-2 border-primary/30 hover:border-primary/60 hover:bg-primary/5">
              <Link to={withRef(`/affirmation-images?remix=${id}`)}>
                <RefreshCw className="h-4 w-4" />
                Remix this style
              </Link>
            </Button>
          </section>

          {/* Share & Download */}
          <section className="flex gap-3 mb-10">
            <Button variant="outline" className="flex-1 gap-2" onClick={handleShare}>
              <Share2 className="h-4 w-4" />
              Share
            </Button>
            <Button variant="outline" className="flex-1 gap-2" onClick={handleDownload}>
              <Download className="h-4 w-4" />
              Download
            </Button>
          </section>

          {/* Viral Loop */}
          <section className="text-center mb-10 py-6 border-t border-b border-border/20">
            <p className="text-sm text-muted-foreground mb-4">
              Who would you create this for?
            </p>
            <Button asChild variant="secondary" size="lg" className="gap-2">
              <Link to={withRef('/try')}>
                Send a message to someone who matters
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </section>

          {/* Footer micro-copy */}
          <footer className="text-center pb-8">
            <p className="text-xs text-muted-foreground/60 mb-1">Made on Say It Now</p>
            <p className="text-[10px] text-muted-foreground/40">
              Real people. Real words. Real connection.
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
}
